import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';
import { confirm } from '@/components/ui/confirm';
import { toast } from 'sonner';
import { Download, Trash2, Eye, Mail, RefreshCw, Users, CheckCircle2, Send, Play } from 'lucide-react';
import { format } from 'date-fns';
import { exportMailMergeToCSV, type MailMergeResult, type OutputType } from './mailmergeExport';
import MailMergeViewDialog from './MailMergeViewDialog';
import MailMergeToCampaignDialog from '@/components/campaigns/MailMergeToCampaignDialog';

interface MailMergeRun {
  id: string;
  name: string;
  outreach_type: string;
  output_type: string;
  status: string;
  total_count: number;
  success_count: number;
  failed_count: number;
  created_at: string;
  results: any;
  custom_prompt: string | null;
}

interface MailMergeManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asPage?: boolean;
  onCampaignCreated?: () => void;
}

const MailMergeManager: React.FC<MailMergeManagerProps> = ({ open, onOpenChange, asPage = false, onCampaignCreated }) => {
  const [runs, setRuns] = useState<MailMergeRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewingRun, setViewingRun] = useState<MailMergeRun | null>(null);
  const [campaignRun, setCampaignRun] = useState<MailMergeRun | null>(null);

  const fetchRuns = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('mailmerge_runs' as any)
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Failed to load mail merge runs');
    } else {
      setRuns((data as any[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) fetchRuns();
  }, [open]);

  const handleDelete = (run: MailMergeRun) => {
    confirm({
      title: 'Delete Mail Merge Run',
      description: `Are you sure you want to delete "${run.name}"? This cannot be undone.`,
      onConfirm: async () => {
        const { error } = await supabase
          .from('mailmerge_runs' as any)
          .delete()
          .eq('id', run.id);
        if (error) {
          toast.error('Failed to delete');
        } else {
          toast.success('Deleted');
          setRuns((prev) => prev.filter((r) => r.id !== run.id));
        }
      },
    });
  };

  const handleExport = (run: MailMergeRun) => {
    const resultsArray: MailMergeResult[] = Array.isArray(run.results) ? run.results : [];
    const map = new Map<string, MailMergeResult>();
    resultsArray.forEach((r) => map.set(r.leadId, r));
    exportMailMergeToCSV(map, run.outreach_type, run.output_type as OutputType);
  };

  const handleRunUpdated = (updatedRun: MailMergeRun) => {
    setRuns((prev) => prev.map((r) => (r.id === updatedRun.id ? updatedRun : r)));
    setViewingRun(updatedRun);
  };

  // Compute stats from runs
  const totalLeads = runs.reduce((sum, r) => sum + r.total_count, 0);
  const totalSuccess = runs.reduce((sum, r) => sum + r.success_count, 0);
  const successRate = totalLeads > 0 ? Math.round((totalSuccess / totalLeads) * 100) : 0;

  const statsCards = (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
      {[
        { label: 'Total Runs', value: runs.length, icon: Mail },
        { label: 'Leads Processed', value: totalLeads, icon: Users },
        { label: 'Success Rate', value: `${successRate}%`, icon: CheckCircle2 },
      ].map((stat) => (
        <Card key={stat.label} elevation="subtle" className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground">{stat.label}</p>
              <p className="text-2xl font-bold tracking-tight mt-1">{stat.value}</p>
            </div>
            <div className="p-2.5 rounded-xl bg-primary/10">
              <stat.icon className="h-5 w-5 text-primary" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const runsList = loading ? (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
      <RefreshCw className="h-6 w-6 animate-spin mb-3" />
      <p className="text-sm">Loading mail merge runs…</p>
    </div>
  ) : runs.length === 0 ? (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
      <div className="p-4 rounded-full bg-muted/50 mb-4">
        <Mail className="h-10 w-10" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-1">No mail merge runs yet</h3>
      <p className="text-sm text-center max-w-sm">
        Select leads from the leads table and generate a mail merge to see your history here.
      </p>
    </div>
  ) : (
    <TooltipProvider>
    <div className="space-y-2">
      {runs.map((run) => (
        <div
          key={run.id}
          className="flex items-center justify-between gap-4 p-4 rounded-lg border border-border/60 hover:bg-muted/30 hover:border-border transition-all duration-200"
        >
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{run.name}</div>
            <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground flex-wrap">
              <span>{format(new Date(run.created_at), 'MMM d, yyyy h:mm a')}</span>
              <Badge variant="outline" size="sm">{run.outreach_type}</Badge>
              <StatusBadge status={run.status} />
              <span className="text-green-600 font-medium">{run.success_count} ✓</span>
              {run.failed_count > 0 && (
                <span className="text-destructive font-medium">{run.failed_count} ✗</span>
              )}
              <span className="text-muted-foreground">/ {run.total_count}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {(run.status === 'in_progress' || run.status === 'partial') && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700" onClick={() => setViewingRun(run)}>
                    <Play className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Continue Generation</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setViewingRun(run)}>
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View details</TooltipContent>
            </Tooltip>
            {(run.output_type === 'email' || run.output_type === 'both') && run.success_count > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setCampaignRun(run)}>
                    <Send className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Send as Campaign</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleExport(run)}>
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export CSV</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={() => handleDelete(run)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete</TooltipContent>
            </Tooltip>
          </div>
        </div>
      ))}
    </div>
    </TooltipProvider>
  );

  const content = (
    <>
      {!loading && runs.length > 0 && statsCards}
      <Card elevation="subtle">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Runs</CardTitle>
        </CardHeader>
        <CardContent>
          {runsList}
        </CardContent>
      </Card>
    </>
  );

  return (
    <>
      {asPage ? (
        <div className="space-y-4 sm:space-y-6">
          {content}
        </div>
      ) : (
        <Dialog open={open && !viewingRun} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Mail Merge History
              </DialogTitle>
              <DialogDescription>View, export, or reopen past mail merge runs.</DialogDescription>
            </DialogHeader>
            <DialogBody>
              {content}
            </DialogBody>
          </DialogContent>
        </Dialog>
      )}

      {viewingRun && (
        <MailMergeViewDialog
          open={!!viewingRun}
          onOpenChange={(val) => { if (!val) setViewingRun(null); }}
          run={viewingRun}
          onRunUpdated={handleRunUpdated}
        />
      )}

      {campaignRun && (
        <MailMergeToCampaignDialog
          open={!!campaignRun}
          onOpenChange={(val) => { if (!val) setCampaignRun(null); }}
          run={campaignRun}
          onCampaignCreated={onCampaignCreated}
        />
      )}
    </>
  );
};

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'completed':
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/30 text-[10px] px-1.5 py-0">Completed</Badge>;
    case 'in_progress':
      return <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30 text-[10px] px-1.5 py-0">In Progress</Badge>;
    case 'partial':
      return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30 text-[10px] px-1.5 py-0">Partial</Badge>;
    case 'failed':
      return <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Failed</Badge>;
    default:
      return <Badge variant="outline" className="text-[10px] px-1.5 py-0">{status}</Badge>;
  }
}

export default MailMergeManager;
